"use client";

import Script from "next/script";
import { usePathname } from "next/navigation";
import { META_PIXEL_ID } from "@/lib/utils/meta-pixel";

/**
 * @file meta-pixel-provider.tsx
 * @description Meta Pixel 초기화 Provider
 *
 * Meta Pixel 스크립트를 로드하고 초기화합니다.
 * RootLayout에서 사용합니다.
 *
 * 주요 기능:
 * 1. Meta Pixel 스크립트 로드
 * 2. Meta Pixel 초기화 (fbq('init'))
 * 3. 전환 이벤트가 중요한 페이지만 허용 (Best Practices 점수 향상, deprecated API 방지)
 *
 * 참고: PageView 추적은 GA에서만 처리됩니다.
 * Meta Pixel은 특정 이벤트(Lead, Purchase 등)만 선택적으로 전송합니다.
 *
 * 사용 방법:
 * ```tsx
 * import { MetaPixelProvider } from '@/components/providers/meta-pixel-provider';
 *
 * export default function RootLayout({ children }) {
 *   return (
 *     <html>
 *       <body>
 *         <MetaPixelProvider />
 *         {children}
 *       </body>
 *     </html>
 *   );
 * }
 * ```
 *
 * @dependencies
 * - next/script: Next.js Script 컴포넌트
 * - next/navigation: usePathname 훅
 * - lib/utils/meta-pixel: META_PIXEL_ID 상수
 */

export function MetaPixelProvider() {
  const pathname = usePathname();
  
  // 필요한 페이지만 명시적으로 허용 (deprecated API 방지)
  // 회원가입, 결제 등 전환 이벤트가 중요한 페이지만 허용
  const allowedPaths = [
    "/sign-up",
    "/sign-in",
  ];
  
  const isAllowed = allowedPaths.some(path => pathname?.startsWith(path));
  
  // 허용된 페이지만 Meta Pixel 로드
  if (!isAllowed) {
    return null;
  }

  return (
    <>
      <Script
        id="meta-pixel"
        strategy="lazyOnload"
        dangerouslySetInnerHTML={{
          __html: `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '${META_PIXEL_ID}');
          `,
        }}
      />
    </>
  );
}
